/**
 * InstructorConfirmDecisionPanel — 1-tap instructor decision panel
 * that appears once an AI offer reaches
 * `status=awaiting_instructor_confirmation` (Track A, Phase E).
 *
 * Operator brief (Feb 13, 2026):
 *   "1-tap decisions:
 *      progress / repeat / step back / discuss.
 *    Save decision to lesson_reviews.instructor_confirmation +
 *    passport_analysis_entries.instructor_confirmation_status.
 *    Notify pupil with final instructor decision/result.
 *    Calm + constructive — never punishment/failure framing."
 *
 * Usage:
 *   <InstructorConfirmDecisionPanel
 *     offerId="ai-offer-..."
 *     reviewIntentLabel="Mock Test Review"
 *     pupilName="Demo Learner"
 *     onConfirmed={(decision) => ...}  // optional refresh hook
 *   />
 *
 * The component fetches its own copy of the offer to render the
 * current state. After a successful confirm it hides itself and
 * fires `onConfirmed(decision)` so parents can refresh their lists.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, RotateCcw, ArrowLeftCircle, MessageCircle,
  Loader2, Sparkles,
} from "lucide-react";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;


const DECISIONS = [
  {
    id: "progress",
    label: "Progress",
    sub: "Move on to the next focus",
    icon: CheckCircle2,
    tone: "emerald",
  },
  {
    id: "repeat",
    label: "Repeat",
    sub: "Take this assessment again",
    icon: RotateCcw,
    tone: "amber",
  },
  {
    id: "step_back",
    label: "Step back",
    sub: "Strengthen the foundation first",
    icon: ArrowLeftCircle,
    tone: "sky",
  },
  {
    id: "discuss",
    label: "Discuss",
    sub: "Talk through it together next lesson",
    icon: MessageCircle,
    tone: "violet",
  },
];

const TONE_CLASSES = {
  emerald: "border-emerald-700/40 bg-emerald-900/15 hover:bg-emerald-900/25 text-emerald-100",
  amber:   "border-amber-700/40 bg-amber-900/15 hover:bg-amber-900/25 text-amber-100",
  sky:     "border-sky-700/40 bg-sky-900/15 hover:bg-sky-900/25 text-sky-100",
  violet:  "border-violet-700/40 bg-violet-900/15 hover:bg-violet-900/25 text-violet-100",
};


export default function InstructorConfirmDecisionPanel({
  offerId, reviewIntentLabel, pupilName, onConfirmed,
}) {
  const [submitting, setSubmitting] = useState(null);
  const [note, setNote] = useState("");
  const [done, setDone] = useState(null);

  const confirm = async (decision) => {
    setSubmitting(decision);
    try {
      await axios.post(
        `${API}/api/ai-offers/${offerId}/confirm`,
        { decision, note: note.trim() || undefined },
        { withCredentials: true },
      );
      toast.success("Confirmed — pupil will see this in-app.");
      setDone(decision);
      onConfirmed?.(decision);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not confirm. Please try again.",
      );
    } finally {
      setSubmitting(null);
    }
  };

  if (done) {
    return (
      <div
        data-testid="instructor-confirm-panel-done"
        className="rounded-2xl border border-emerald-700/40 bg-emerald-900/15 p-4 flex items-center gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-[13px] font-extrabold text-emerald-100">
            Confirmed — {pupilName || "your pupil"} will see this in-app.
          </p>
          <p className="text-[11.5px] text-emerald-200/70 mt-0.5">
            Decision: {DECISIONS.find((d) => d.id === done)?.label}.
          </p>
        </div>
      </div>
    );
  }

  // Media quarantine — silent. No CTA into POST /api/ai-offers/{id}/confirm.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <section
      data-testid="instructor-confirm-panel"
      className="rounded-2xl border border-indigo-700/40 bg-slate-900/60 p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-indigo-300" />
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
          {reviewIntentLabel || "AI review"} — pick the next step
        </p>
      </div>
      <p className="text-[12px] text-slate-300 leading-relaxed">
        Pick what should happen next for {pupilName || "your pupil"}.
        One tap saves it, the pupil sees it, and you can talk it
        through in your next lesson.
      </p>

      <div className="grid grid-cols-2 gap-2">
        {DECISIONS.map((d) => {
          const Icon = d.icon;
          return (
            <button
              key={d.id}
              type="button"
              data-testid={`instructor-confirm-${d.id}`}
              disabled={!!submitting}
              onClick={() => confirm(d.id)}
              className={
                "rounded-xl border p-3 text-left active:scale-[0.99] transition disabled:opacity-50 "
                + TONE_CLASSES[d.tone]
              }
            >
              <div className="flex items-center gap-1.5">
                {submitting === d.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Icon className="w-3.5 h-3.5" />}
                <p className="text-[12.5px] font-extrabold">{d.label}</p>
              </div>
              <p className="text-[11px] opacity-80 mt-1 leading-snug">
                {d.sub}
              </p>
            </button>
          );
        })}
      </div>

      <div>
        <label className="text-[11px] text-slate-400 font-extrabold">
          Quick note (optional, 240 chars)
        </label>
        <textarea
          rows={2}
          maxLength={240}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="instructor-confirm-note"
          placeholder="e.g. Nice control on the roundabout — let's build on it."
          className="mt-1.5 w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2 text-[12.5px] text-white focus:outline-none focus:border-indigo-500"
        />
      </div>
    </section>
  );
}
